import {
  Checkbox,
  Flex,
  Heading,
  Skeleton,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { Trans } from "@lingui/macro";
import {
  type ProjectGraphQuery,
  ProjectTypeEnum,
  useProjectGraphQuery,
} from "@src/__generated__/graphql";
import { onError } from "@src/utils/apollo";
import { observer } from "mobx-react-lite";
import { useState } from "react";
import { useStore } from "../Store";
import ProjectGraph from "./ProjectGraph";

const GraphCard = () => {
  const store = useStore();
  const [totalBudgetCheckbox, setTotalBudgetCheckbox] = useState(true);
  const [trackedBudgetCheckbox, setTrackedBudgetCheckbox] = useState(true);

  const [projectGraphData, setProjectGraphData] = useState<
    ProjectGraphQuery["projectGraph"] | undefined
  >(undefined);

  const { data, loading, refetch } = useProjectGraphQuery({
    variables: {
      projectId: store.project?.id!,
    },
    skip: !store.project,
    ...onError(),
    onCompleted(data) {
      setProjectGraphData(data?.projectGraph);
    },
  });

  store.refetchGraph = refetch;

  const intervalEnd = new Date(
    store.project?.finished_at ?? store.project?.archived_at ?? new Date(),
  );

  if (!data?.projectGraph || !projectGraphData) {
    if (loading) {
      return <Skeleton h="350px" mb="8" rounded="lg" size="xl" />;
    }
    return null;
  }

  const isTimeAndMaterials =
    store.project?.type === ProjectTypeEnum.TimeMaterials;

  return (
    <>
      <Flex
        align={{ base: "flex-start", md: "center" }}
        direction={{ base: "column", md: "row" }}
        gap={{ base: "4", md: "2" }}
      >
        <Heading size="md">
          <Text fontSize="inherit" fontWeight="inherit">
            {store.project?.title}&nbsp;
            <Text as="span" whiteSpace="nowrap">
              ({store.project?.code})
            </Text>
          </Text>
        </Heading>
        <Spacer />
        <Flex
          justify={{ base: "space-between", md: "initial" }}
          direction={{ base: "column-reverse", sm: "row" }}
          gap="5"
          w={{ base: "full", md: "max-content" }}
        >
          <Flex justify="flex-end" gap="4">
            {!isTimeAndMaterials && (
              <Checkbox
                colorScheme="yellow"
                isChecked={totalBudgetCheckbox}
                isDisabled={totalBudgetCheckbox && !trackedBudgetCheckbox}
                onChange={({ target }) => {
                  setTotalBudgetCheckbox(target.checked);
                }}
                size="lg"
              >
                <Trans>Budget</Trans>
              </Checkbox>
            )}
            <Checkbox
              colorScheme="teal"
              isChecked={trackedBudgetCheckbox}
              isDisabled={trackedBudgetCheckbox && !totalBudgetCheckbox}
              onChange={({ target }) => {
                setTrackedBudgetCheckbox(target.checked);
              }}
              size="lg"
            >
              <Trans>Tracked</Trans>
            </Checkbox>
          </Flex>
        </Flex>
      </Flex>
      <ProjectGraph
        data={projectGraphData}
        intervalStart={store.project!.start_date}
        intervalEnd={intervalEnd}
        isTimeAndMaterials={isTimeAndMaterials}
        totalBudgetCheckbox={totalBudgetCheckbox}
        trackedBudgetCheckbox={trackedBudgetCheckbox}
      />
    </>
  );
};

export default observer(GraphCard);
